import { useFieldArray, useFormContext } from 'react-hook-form'
import {
  CurecallButton,
  CurecallSelect,
  CurecallSelectItem,
  ECurecallButtonAppearance,
  PlusIcon,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { EAgeGroup, TMessageContentRule, TProgrammedMessageFormValues } from './programmed-message.type'
import MessageContentRuleCard from './message-content-rule-card.component'
import { getDoctorName } from 'src/utils/string-formatter.util'
import { useLocale } from 'src/hooks/use-locale'
import { TLabel, TPatientLocale } from 'src/types/locales'

export default function MessageFields() {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { activeDoctors } = useCurrentProject()
  const { control, getValues } = useFormContext<TProgrammedMessageFormValues>()

  const { fields, append, remove } = useFieldArray<TProgrammedMessageFormValues, 'profileRules'>({
    control,
    name: 'profileRules',
  })

  const [newDoctorOption, setNewDoctorOption] = useState<CurecallSelectItem<string | null> | undefined>(
    undefined,
  )
  const [newAgeOption, setNewAgeOption] = useState<CurecallSelectItem<EAgeGroup | null> | undefined>(
    undefined,
  )
  const [pendingEditIndex, setPendingEditIndex] = useState<number | null>(null)

  useEffect(() => {
    if (pendingEditIndex !== null) {
      setPendingEditIndex(null)
    }
  }, [pendingEditIndex])

  const doctorOptions: CurecallSelectItem<string | null>[] = [
    { value: null, label: t('pages.configuration.messages.all-doctors') },
    ...activeDoctors.map((doctor) => ({
      value: String(doctor.id),
      label: getDoctorName(doctor),
    })),
  ]

  const ageOptions: CurecallSelectItem<EAgeGroup | null>[] = [
    { value: null, label: t('pages.configuration.messages.all-ages') },
    { value: EAgeGroup.MINOR, label: t('pages.configuration.messages.minor') },
    { value: EAgeGroup.MAJOR, label: t('pages.configuration.messages.adult') },
  ]

  const handleAddCase = () => {
    if (!newDoctorOption || !newAgeOption) return
    append({
      message: { [locale]: '' } as TLabel,
      medicalStaffMemberId: newDoctorOption.value,
      ageGroup: newAgeOption.value,
    })
    setNewDoctorOption(undefined)
    setNewAgeOption(undefined)
  }

  const handleDuplicate = (index: number) => {
    const source: TMessageContentRule = getValues(`profileRules.${index}`)
    append({
      message: { [locale]: source.message?.[locale as TPatientLocale] ?? '' } as TLabel,
      medicalStaffMemberId: source.medicalStaffMemberId,
      ageGroup: source.ageGroup,
    })
    setPendingEditIndex(fields.length)
  }

  return (
    <div className="c-flex c-flex-col c-gap-3">
      {fields.map((field, index) => (
        <MessageContentRuleCard
          key={field.id}
          index={index}
          onRemove={() => remove(index)}
          isRemoveDisabled={fields.length === 1}
          onDuplicate={() => handleDuplicate(index)}
          initiallyEditing={pendingEditIndex === index}
        />
      ))}

      <div className="c-rounded-xl c-border c-border-dashed c-border-grey-400 c-p-4 c-flex c-flex-col c-gap-3">
        <p className="c-m-0 c-font-nexa c-text-sm c-font-bold c-text-grey-700">
          {t('pages.configuration.messages.add-case')}
        </p>
        <div className="c-flex c-items-center c-gap-2 c-flex-wrap">
          <CurecallSelect
            items={doctorOptions}
            selectedItem={newDoctorOption}
            onChange={setNewDoctorOption}
            placeholder={t('pages.configuration.messages.select-doctor')}
            wrapperClassName="c-w-52 c-relative"
          />
          <PlusIcon className="c-h-3 c-w-3 c-text-grey-500" />
          <CurecallSelect
            items={ageOptions}
            selectedItem={newAgeOption}
            onChange={setNewAgeOption}
            placeholder={t('pages.configuration.messages.select-age')}
            wrapperClassName="c-w-44 c-relative"
          />
          <CurecallButton
            type="button"
            appearance={ECurecallButtonAppearance.SECONDARY}
            isDisabled={!newDoctorOption || !newAgeOption}
            onClick={handleAddCase}
          >
            {t('pages.configuration.messages.add-this-case')}
          </CurecallButton>
        </div>
      </div>
    </div>
  )
}
